import { Component, OnInit, OnDestroy, HostListener, ViewEncapsulation, ElementRef } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { AuthService } from 'app/core/auth/auth.service';
import { UserService } from 'app/core/services/auth/user.service';
import { ThemeService } from 'app/core/config/theme.service';
import { PermissionService } from 'app/core/permission/permission.service';
import { SidebarNavItem, defaultNavigation } from 'app/core/navigation';
import { Subscription, filter } from 'rxjs';

@Component({
    selector     : 'layout',
    templateUrl  : './layout.component.html',
    styleUrls    : ['./layout.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class LayoutComponent implements OnInit, OnDestroy
{
    sidebarCollapsed = false;
    mobileSidebarOpen = false;
    isMobile = false;
    currentRoute = '';
    userName = 'User';
    userEmail = '';
    userInitials = 'U';
    userRole = 'Staff';
    userAvatar = '';
    userDropdownOpen = false;

    isDark$ = this.themeService.isDark$;

    // Data menu navigasi dasar
    navItems: SidebarNavItem[] = defaultNavigation;

    // Data menu navigasi yang telah difilter (dicache agar tidak membuat array baru di setiap CD tick)
    filteredNavItems: SidebarNavItem[] = [];

    // State tracking submenu yang sedang terbuka (default tertutup semua saat reload/navigasi)
    expandedMenus: { [key: string]: boolean } = {};

    private routerSub!: Subscription;
    private permissionSub!: Subscription;
    private userSub!: Subscription;

    constructor(
        private authService: AuthService,
        private userService: UserService,
        private permissionService: PermissionService,
        private router: Router,
        private themeService: ThemeService,
        private elementRef: ElementRef
    ) {}

    ngOnInit(): void {
        this.themeService.applyStoredTheme();
        this.checkScreenSize();
        this.loadUserInfo();
        this.updateFilteredNavItems();

        this.currentRoute = this.router.url;

        this.routerSub = this.router.events
            .pipe(filter(event => event instanceof NavigationEnd))
            .subscribe((event: any) => {
                this.currentRoute = event.urlAfterRedirects || event.url;
                this.userDropdownOpen = false;
                // Auto-close mobile sidebar on navigate
                if (this.isMobile) {
                    this.mobileSidebarOpen = false;
                }
            });

        // Reaktif ketika status permissions berubah
        this.permissionSub = this.permissionService.permissions$.subscribe(() => {
            this.updateFilteredNavItems();
        });

        // Reaktif ketika data user diperbarui dari auth/me
        this.userSub = this.userService.user$.subscribe((user) => {
            if (user) {
                this.updateUserData(user);
            }
        });
    }

    private updateFilteredNavItems(): void {
        this.filteredNavItems = this.filterNavItems(this.navItems);
    }

    trackByNavId(index: number, item: SidebarNavItem): string {
        return item.id || item.route;
    }

    ngOnDestroy(): void {
        if (this.routerSub) {
            this.routerSub.unsubscribe();
        }
        if (this.permissionSub) {
            this.permissionSub.unsubscribe();
        }
        if (this.userSub) {
            this.userSub.unsubscribe();
        }
    }

    @HostListener('window:resize')
    onResize(): void {
        this.checkScreenSize();
    }

    /**
     * Mengembalikan nama halaman aktif untuk breadcrumb header
     */
    get currentBreadcrumb(): string {
        // Approvals
        if (this.currentRoute.startsWith('/approvals/leaves')) return 'Approval Cuti & Izin';
        if (this.currentRoute.startsWith('/approvals/overtimes')) return 'Approval Lembur';
        if (this.currentRoute.startsWith('/approvals/reimbursements')) return 'Approval Reimbursement';
        if (this.currentRoute.startsWith('/approvals/business-trips')) return 'Approval Perjalanan Dinas';
        if (this.currentRoute.startsWith('/approvals/vehicle-bookings')) return 'Approval Peminjaman Kendaraan';
        if (this.currentRoute.startsWith('/approvals')) return 'Pusat Persetujuan';

        // Self-Service
        if (this.currentRoute.startsWith('/self-service/leaves')) return 'Pengajuan Cuti & Izin';
        if (this.currentRoute.startsWith('/self-service/overtimes')) return 'Pengajuan Lembur';
        if (this.currentRoute.startsWith('/self-service/reimbursements')) return 'Pengajuan Reimbursement';
        if (this.currentRoute.startsWith('/self-service/business-trips')) return 'Pengajuan Perjalanan Dinas';
        if (this.currentRoute.startsWith('/self-service/vehicle-bookings')) return 'Pengajuan Peminjaman Kendaraan';
        if (this.currentRoute.startsWith('/self-service')) return 'Portal Pengajuan';

        // Master Data
        if (this.currentRoute.startsWith('/master-data/division')) return 'Data Divisi';
        if (this.currentRoute.startsWith('/master-data/company')) return 'Data Company';
        if (this.currentRoute.startsWith('/master-data')) return 'Master Data';

        // Users
        if (this.currentRoute === '/users/verification' || this.currentRoute.startsWith('/users/verification')) return 'Verifikasi User & Karyawan Baru';
        if (this.currentRoute === '/users/roles' || this.currentRoute.startsWith('/users/roles')) return 'Roles User';
        if (this.currentRoute === '/users/permissions' || this.currentRoute.startsWith('/users/permissions')) return 'Permission Role';
        if (this.currentRoute === '/users/approval-rules' || this.currentRoute.startsWith('/users/approval-rules')) return 'Approval Rules';
        if (this.currentRoute.startsWith('/users')) return 'User Account';
        if (this.currentRoute.startsWith('/403')) return 'Akses Ditolak';
        return 'Dashboard';
    }

    private filterNavItems(items: SidebarNavItem[]): SidebarNavItem[] {
        return items
            .filter((item) => {
                if (!item.permission) {
                    return true;
                }
                return this.permissionService.hasAccess(
                    item.permission,
                    item.permissionStrategy || 'ANY'
                );
            })
            .map((item) => {
                if (item.children && item.children.length > 0) {
                    return {
                        ...item,
                        children: this.filterNavItems(item.children),
                    };
                }
                return item;
            });
    }

    private checkScreenSize(): void {
        this.isMobile = window.innerWidth < 1024;
        if (this.isMobile) {
            this.sidebarCollapsed = false;
            this.mobileSidebarOpen = false;
        }
    }

    private loadUserInfo(): void {
        const user = this.authService.getCurrentUser();
        if (user) {
            this.updateUserData(user);
        }
    }

    private updateUserData(user: any): void {
        this.userName = user.name || 'User';
        this.userEmail = user.email || '';
        this.userInitials = this.getInitials(this.userName);
        this.userAvatar = user.avatar || '';
        this.userRole = this.getUserRoleName(user);
    }

    private getUserRoleName(user: any): string {
        if (!user) return 'Staff';
        if (typeof user.role === 'string' && user.role.trim() !== '') return user.role;
        if (user.role && typeof user.role === 'object' && user.role.name) return user.role.name;
        if (Array.isArray(user.roles) && user.roles.length > 0) {
            const first = user.roles[0];
            return typeof first === 'string' ? first : (first.name || 'Staff');
        }
        return 'Staff';
    }

    private getInitials(name: string): string {
        return name
            .split(' ')
            .map(n => n[0])
            .join('')
            .substring(0, 2)
            .toUpperCase();
    }

    toggleSidebar(): void {
        if (this.isMobile) {
            this.mobileSidebarOpen = !this.mobileSidebarOpen;
        } else {
            this.sidebarCollapsed = !this.sidebarCollapsed;
        }
    }

    closeMobileSidebar(): void {
        this.mobileSidebarOpen = false;
    }

    toggleUserDropdown(event: MouseEvent): void {
        event.stopPropagation();
        this.userDropdownOpen = !this.userDropdownOpen;
    }

    closeUserDropdown(): void {
        this.userDropdownOpen = false;
    }

    @HostListener('document:click', ['$event'])
    onDocumentClick(event: MouseEvent): void {
        if (this.userDropdownOpen && !this.elementRef.nativeElement.contains(event.target)) {
            this.userDropdownOpen = false;
        }
    }

    @HostListener('document:keydown.escape')
    onEscapeKey(): void {
        if (this.userDropdownOpen) {
            this.userDropdownOpen = false;
        }
    }

    toggleSubmenu(item: SidebarNavItem, event?: MouseEvent): void {
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }
        if (this.sidebarCollapsed) {
            this.sidebarCollapsed = false;
        }
        const isCurrentlyOpen = !!this.expandedMenus[item.id];
        // Tutup semua submenu lain (accordion mode)
        this.expandedMenus = {};
        // Toggle item yang diklik
        if (!isCurrentlyOpen) {
            this.expandedMenus[item.id] = true;
        }
    }

    isSubmenuOpen(item: SidebarNavItem): boolean {
        return !!this.expandedMenus[item.id];
    }

    isItemActive(item: SidebarNavItem): boolean {
        if (item.children && item.children.length > 0) {
            return item.children.some(c => this.isChildActive(c));
        }
        return this.currentRoute === item.route;
    }

    isChildActive(child: SidebarNavItem): boolean {
        if (child.route === '/users') {
            return this.currentRoute === '/users' || this.currentRoute === '/users/';
        }
        return this.currentRoute.startsWith(child.route);
    }

    onNavItemClick(): void {
        if (this.isMobile) {
            this.mobileSidebarOpen = false;
        }
    }

    navigateTo(route: string): void {
        this.closeUserDropdown();
        this.router.navigate([route]);
    }

    onLogout(): void {
        this.closeUserDropdown();
        this.authService.signOut().subscribe(() => {
            this.router.navigate(['/sign-in']);
        });
    }

    /** 🌓 Toggle Slide Mode Terang <-> Gelap */
    toggleScheme(): void {
        this.themeService.toggleScheme();
    }
}
